import { CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useNavigate } from 'react-router-dom';

export default function TopPartsTable({ parts, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top 10 At-Risk Parts</CardTitle>
          <CardDescription>Highest tariff exposure</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!parts || parts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Top 10 At-Risk Parts</CardTitle>
          <CardDescription>Highest tariff exposure</CardDescription>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
          <span className="text-gray-500">No at-risk parts found</span>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'high_risk':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertCircle size={12} />
            High Risk
          </Badge>
        );
      case 'medium_risk':
        return (
          <Badge variant="outline" className="text-orange-600 border-orange-600 flex items-center gap-1">
            <AlertTriangle size={12} />
            Medium
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-green-600 border-green-600 flex items-center gap-1">
            <CheckCircle2 size={12} />
            Low
          </Badge>
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Top 10 At-Risk Parts</CardTitle>
        <CardDescription>Click a row to view details</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part Number</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>OEM</TableHead>
                <TableHead className="text-right">Tariff</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parts.map((part, index) => (
                <TableRow
                  key={part.part_number}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/opportunities`)}
                >
                  <TableCell className="font-medium">{part.part_number}</TableCell>
                  <TableCell className="max-w-xs truncate">{part.description}</TableCell>
                  <TableCell>{part.oem}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(part.tariff_cents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {part.effective_rate ? `${part.effective_rate.toFixed(1)}%` : 'N/A'}
                  </TableCell>
                  <TableCell>{getStatusBadge(part.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
